import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturb';
import EditNoteIcon from '@mui/icons-material/EditNote';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import RunningWithErrorsOutlinedIcon from '@mui/icons-material/RunningWithErrorsOutlined';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { Chip, SvgIconProps, SxProps, Theme, Tooltip, useTheme } from '@mui/material';
import { TFunction } from 'i18next';
import { BroadcastMessageItem } from 'providers/contexts/extensions/Broadcast';
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { bloxColors, bloxValues } from 'themes/constants';
import { getColorMix } from 'utility/styles';

/*
 * -----------------------------------
 * Notifier - Status Badge
 * -----------------------------------
 */

/**
 * getMessageStatusInfo
 * status color, string, icons based on send_status
 * translates status string via i18n (needs t passed for reactivity)
 */

type MessageStatusInfo = {
  color: string;
  status: string | null;
  info?: string | null; // tooltip info
  icon: React.ComponentType<SvgIconProps> | null;
};

type GetMessageStatusInfoArgs = {
  sendStatus: string | null | undefined;
  theme: Theme;
  t: TFunction;
};

export function getMessageStatusInfo({
  sendStatus,
  theme,
  t,
}: GetMessageStatusInfoArgs): MessageStatusInfo {
  // If null, likely creating new item...
  if (!sendStatus) {
    return {
      color: theme.palette.text.secondary,
      status: null,
      icon: null,
    };
  }

  // Normalize for switch
  const status = sendStatus?.toLowerCase() ?? '';

  // Bump lightness on certain colors in dark mode
  const isDarkMode = theme?.palette?.mode === 'dark';

  const primaryBlue = isDarkMode
    ? theme.palette.primary.light
    : theme.palette.primary.main;

  const partialColor = isDarkMode ? bloxColors.violet : bloxColors.purple;

  switch (status) {
    case 'draft': {
      return {
        color: primaryBlue,
        status: t('blox.notifier.status.draft'),
        icon: EditNoteIcon,
      };
    }
    case 'sent': {
      return {
        color: theme.palette.success.main,
        status: t('blox.notifier.status.sent'),
        icon: CheckCircleIcon,
      };
    }
    case 'pending': {
      return {
        color: bloxColors.darkYellow,
        status: t('blox.notifier.status.pending'),
        icon: ScheduleIcon,
      };
    }
    case 'staged': {
      return {
        color: bloxColors.darkYellow,
        status: t('blox.notifier.status.staged'),
        icon: ScheduleIcon,
      };
    }
    case 'incomplete': {
      return {
        color: partialColor,
        status: t('blox.notifier.status.partially_sent'),
        info: t('blox.notifier.status.partially_sent_info'),
        icon: RunningWithErrorsOutlinedIcon,
      };
    }
    case 'failed': {
      return {
        color: theme.palette.error.main,
        status: t('blox.notifier.status.failed'),
        icon: ErrorOutlineIcon,
      };
    }
    case 'canceled':
    case 'cancelled': {
      return {
        color: theme.palette.error.main,
        status: t('blox.notifier.status.canceled'),
        icon: DoNotDisturbIcon,
      };
    }
    case 'undelivered': {
      return {
        color: theme.palette.error.main,
        status: t('blox.notifier.status.undelivered'),
        icon: ErrorOutlineIcon,
      };
    }
    default: {
      return {
        color: theme.palette.info.main,
        status: sendStatus || t('blox.notifier.status.unknown'),
        icon: QuestionMarkIcon,
      };
    }
  }
}

export type NotifierStatusBadgeProps = {
  messageItem?: BroadcastMessageItem | null;
  compact?: boolean;
  propSx?: SxProps<Theme>;
};

/**
 * NotifierStatusBadge Component
 * Used in notifier search, and notifier modals
 */
export const NotifierStatusBadge = memo(
  ({ messageItem, compact, propSx }: NotifierStatusBadgeProps) => {
    const { t } = useTranslation();
    const theme = useTheme();

    // extract mesage for css / ui details
    const badgeInfo = useMemo(() => {
      return getMessageStatusInfo({
        sendStatus: messageItem?.send_status,
        theme: theme,
        t: t,
      });
    }, [messageItem?.send_status, theme, t]);

    const statusColor = badgeInfo?.color;
    const strStatus = badgeInfo?.status;
    const strTooltip = badgeInfo?.info || '';
    const Icon = badgeInfo?.icon;

    // with text this small, doing fine tuning for legibility
    const statusBadgeSx: SxProps<Theme> = useMemo(() => {
      const isDarkMode = theme?.palette?.mode === 'dark';

      const blackWhite = isDarkMode
        ? theme.palette.common.black
        : theme.palette.common.white;

      const bgMixPct = isDarkMode ? 16 : 8;
      const borderMixPct = isDarkMode ? 24 : 10;

      const fontSize = compact ? bloxValues.tinyFontSizeRem : bloxValues.smallFontSizeRem;

      const iconSize = compact
        ? bloxValues.tinyFontSizeRem
        : bloxValues.regularFontSizeRem;

      const pxSize = compact ? '0px' : '2px';

      return {
        color: statusColor,
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: getColorMix(statusColor, borderMixPct, blackWhite, 'srgb'),
        bgcolor: getColorMix(statusColor, bgMixPct, blackWhite, 'srgb'),
        fontSize: fontSize,
        fontWeight: bloxValues.fontWeightMedium,
        px: pxSize,
        textTransform: 'capitalize',
        '.MuiChip-icon': {
          color: 'inherit',
          fontSize: iconSize,
        },
        ...(typeof propSx === 'function' ? propSx(theme) : propSx),
      };
    }, [compact, propSx, statusColor, theme]);

    if (!strStatus) {
      return null;
    }

    return (
      <Tooltip
        enterDelay={200}
        title={strTooltip}
      >
        <Chip
          data-testid="notifier-status-badge"
          icon={Icon ? <Icon /> : undefined}
          label={strStatus}
          size="small"
          variant="filled"
          sx={statusBadgeSx}
        />
      </Tooltip>
    );
  },
);
NotifierStatusBadge.displayName = 'NotifierStatusBadge';
